import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import type { ReadableSpan, SpanExporter } from '@opentelemetry/sdk-trace-base';
import { ExportResultCode, W3CTraceContextPropagator } from '@opentelemetry/core';
import type { ExportResult } from '@opentelemetry/core';
import { propagation } from '@opentelemetry/api';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_DEPLOYMENT_ENVIRONMENT } from '@opentelemetry/semantic-conventions/incubating';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { JsonTraceSerializer } from '@opentelemetry/otlp-transformer';
import { proxyTraces } from '@ordr-as/ordr-service-types/epos';
import config from './config';
import { isAdyenDevice } from './utils/isAdyenDevice';

let currentLocationId: number | undefined;
let paymentTracingActive = false;

export function setTracingLocationId(id: number) {
  currentLocationId = id;
}

export function setPaymentTracingActive(active: boolean) {
  paymentTracingActive = active;
}

if (!['local', 'localQa', 'localProd'].includes(config.env) && !isAdyenDevice()) {
  const isTracingAllowed = () => {
    const { tracingLocationIds } = config;
    if (tracingLocationIds === null) return true;
    return currentLocationId !== undefined && tracingLocationIds.includes(currentLocationId);
  };

  class OrdrServiceTraceExporter implements SpanExporter {
    export(spans: ReadableSpan[], resultCallback: (result: ExportResult) => void): void {
      if (!isTracingAllowed()) {
        resultCallback({ code: ExportResultCode.SUCCESS });
        return;
      }
      const paymentSpans = spans.filter((s) => s.attributes['app.payment_span'] === true);
      if (paymentSpans.length === 0) {
        resultCallback({ code: ExportResultCode.SUCCESS });
        return;
      }
      const serialized = JsonTraceSerializer.serializeRequest(paymentSpans);
      if (!serialized) {
        resultCallback({ code: ExportResultCode.SUCCESS });
        return;
      }
      proxyTraces(JSON.parse(new TextDecoder().decode(serialized)) as string)
        .then(() => resultCallback({ code: ExportResultCode.SUCCESS }))
        .catch((error: unknown) => resultCallback({ code: ExportResultCode.FAILED, error: error as Error }));
    }

    shutdown(): Promise<void> {
      return Promise.resolve();
    }
  }

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: 'epos-frontend',
      [ATTR_DEPLOYMENT_ENVIRONMENT]: config.env,
    }),
    spanProcessors: [new BatchSpanProcessor(new OrdrServiceTraceExporter())],
  });

  propagation.setGlobalPropagator(new W3CTraceContextPropagator());
  provider.register();

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        semconvStabilityOptIn: 'http',
        propagateTraceHeaderCorsUrls: [
          new RegExp(config.bff.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
          new RegExp(config.ordrServiceUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
          new RegExp(config.menuBuilderUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
        ],
        ignoreUrls: [/\/oltp-proxy\//],
        applyCustomAttributesOnSpan(span, request) {
          const url = request instanceof Request ? request.url : undefined;
          const method = request instanceof Request ? request.method : (request as RequestInit).method;

          if (currentLocationId !== undefined) span.setAttribute('app.location_id', currentLocationId);
          if (paymentTracingActive && method === 'POST') {
            span.setAttribute('app.payment_span', true);
          }

          if (!url) return;

          const entry = (performance.getEntriesByType('resource') as PerformanceResourceTiming[]).findLast(
            (e) => e.name === url
          );
          if (!entry) return;

          const ttfb = entry.responseStart - entry.requestStart;
          const tcp = entry.connectEnd - entry.connectStart;
          const dns = entry.domainLookupEnd - entry.domainLookupStart;

          if (ttfb > 0) span.setAttribute('network.ttfb_ms', ttfb);
          if (tcp > 0) span.setAttribute('network.tcp_ms', tcp);
          if (dns > 0) span.setAttribute('network.dns_ms', dns);
        },
      }),
    ],
  });
}
